import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { usePatientCase } from "../usePatientCase";
import { useCallback } from "react";
import { QualifiedFeature, QualifiedFeatureFragment } from "fe-common/graphql";
import {
  DataInput,
  extractResponseFromQF,
  responsesAsChangeOperation,
  sameDataInputQualifiedFeature,
} from "../../dataInputTypes";
import {
  PanelContext,
  PatientCase,
  PatientCaseGrouping,
} from "../../../context/diagnosticAgent";
import { DataInputSectionConfigurations } from "../config/dataInputSectionConfiguration";
import { KahunPanelSubGroup, Relations } from "shared";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { startCase } from "lodash";

export const useChangePatientCase = () => {
  const { inWizard } = useDiagnosticNavigation();
  const {
    pendingFindings,
    patientCase,
    doChangesToPatientCase,
  } = usePatientCase();

  const setValue = useCallback(
    (
      qf: QualifiedFeatureFragment,
      response: DataInput,
      section: PatientCaseGrouping,
      panelContext: PanelContext,
      pending: boolean = false,
      log: boolean = true,
    ) => {
      const changes = responsesAsChangeOperation({ qf, response, section });

      if (pending) {
        doChangesToPatientCase(changes, true, pending);
      } else {
        doChangesToPatientCase(changes);
      }

      if (!log) {
        return;
      }

      const subGroupName =
        DataInputSectionConfigurations[panelContext.panelCategory.id]
          .displayName;

      const location = `${startCase(section)}/${subGroupName}${
        panelContext.panelCategory.name ? "/" + panelContext.panel.title : ""
      }`;

      let qfValue = qf.qualifiers.find(
        (qualifier) => qualifier.relation.name === qf.name,
      );

      let isProcedure = false;
      if (panelContext.panelCategory.id === KahunPanelSubGroup.Procedures) {
        qfValue = qf.qualifiers.find(
          (qualifier) => qualifier.relation.id === Relations.PROCEDURE_FINDING,
        );
        isProcedure = true;
      }

      const getNegateText = () => {
        if (isProcedure) {
          return response
            ? response.negate === true
              ? "Negative "
              : "Positive "
            : "";
        } else {
          return response && response.negate === true ? "No " : "";
        }
      };

      const action = `${response ? "Check " : "Uncheck "}`;
      const finding = `${getNegateText()}${
        response?.level ? response?.level.toUpperCase() + " " : ""
      }${qf && qf.name ? qf.name + " " : ""}${
        qfValue && qfValue.value ? qfValue.value.name : ""
      }`;
      const label = location + " => " + action + finding;

      // events
      evt({
        category: inWizard
          ? CATEGORY.AGENT_WIZARD
          : CATEGORY.AGENT_PATIENT_VIEW,
        action: panelContext.fromSearch
          ? ACTION.SELECT_FEATURE_FROM_TAB_SEARCH
          : ACTION.SELECT_FEATURE_FROM_TAB_SUGGESTED,
        label: label,
      });
    },
    [doChangesToPatientCase, inWizard],
  );

  const setCaseValue = useCallback(
    (
      qf: QualifiedFeatureFragment,
      response: DataInput,
      section: PatientCaseGrouping,
      panelContext: PanelContext,
      log: boolean = true,
    ) => setValue(qf, response, section, panelContext, false, log),
    [setValue],
  );

  const setCaseValuePending = useCallback(
    (
      qf: QualifiedFeatureFragment,
      response: DataInput,
      section: PatientCaseGrouping,
      panelContext: PanelContext,
      log: boolean = true,
    ) => setValue(qf, response, section, panelContext, true, log),
    [setValue],
  );

  const getValue = useCallback(
    (
      toUpdate: PatientCase,
      qf: QualifiedFeature,
      section: PatientCaseGrouping,
      subGroupId?: string,
    ) => {
      let existingValue = extractResponseFromQF(
        toUpdate[section]?.find((c) => sameDataInputQualifiedFeature(c, qf)),
      );

      if (!existingValue) {
        existingValue = extractResponseFromQF(
          toUpdate[PatientCaseGrouping.MAIN]?.find((c) =>
            sameDataInputQualifiedFeature(c, qf),
          ),
        );
      }

      // look in responses in state, and if not there, in the patient case
      return existingValue;
    },
    [],
  );

  const getCaseValue = useCallback(
    (
      qf: QualifiedFeature,
      section: PatientCaseGrouping,
      subGroupId?: string,
    ) => {
      return getValue(patientCase, qf, section, subGroupId);
    },
    [patientCase, getValue],
  );

  const getCaseValuePending = useCallback(
    (
      qf: QualifiedFeature,
      section: PatientCaseGrouping,
      subGroupId?: string,
    ) => {
      return getValue(pendingFindings, qf, section, subGroupId);
    },
    [pendingFindings, getValue],
  );

  return {
    setCaseValue: setCaseValue,
    getCaseValue: getCaseValue,
    getCaseValuePending: getCaseValuePending,
    setCaseValuePending: setCaseValuePending,
  };
};
